import { useState, useRef, useEffect, useId } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UNAVAILABLE_LABEL,
  initialFocusIndex,
  isModelUnavailable,
  modelOptions,
  nextEnabledIndex,
  unavailableTooltip,
} from '../utils/modelAvailability.js';
import { Badge } from './Badge';
import './ModelSelector.css';

function ModelIcon() {
  return (
    <svg className="model-selector__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 17L12 22L22 17" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 12L12 17L22 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/** Badge « Indisponible » du modèle choisi, raison en infobulle (contrat v1.6). */
function UnavailableBadge({ model }) {
  return (
    <Badge tone="warn" className="model-selector__badge" title={unavailableTooltip(model)}>
      {UNAVAILABLE_LABEL}
    </Badge>
  );
}

/**
 * Choix du modèle (pied du compositeur). Contrat v1.6 : un modèle dont `disponible` vaut
 * false reste listé mais ne peut pas être choisi (aria-disabled, hors tabulation, ignoré
 * par les flèches), avec le badge « Indisponible » et la raison en infobulle ; si le modèle
 * choisi devient indisponible, le badge s'affiche à côté de son nom.
 * Clavier : flèches haut / bas depuis le bouton ou dans le menu, Début / Fin, Échap.
 */
export function ModelSelector({ models, selectedModel, onSelect, disabled }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const optionRefs = useRef([]);
  // Ouverture au clavier : le focus passe sur la sélection (ou la première option activable)
  const focusOnOpenRef = useRef(false);
  const reasonId = useId();

  const options = modelOptions(models, selectedModel?.id);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Fermer le menu dès qu'une réponse démarre
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  // Fermeture au clic extérieur ou avec Échap
  useEffect(() => {
    if (!open) return;
    const handlePointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !focusOnOpenRef.current) return undefined;
    focusOnOpenRef.current = false;
    const frame = requestAnimationFrame(() => optionRefs.current[initialFocusIndex(optionsRef.current)]?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  // /api/models indisponible : pas de sélecteur, le serveur utilise son modèle par défaut
  if (!selectedModel) return null;
  const selectedUnavailable = isModelUnavailable(selectedModel);

  // Un seul modèle : afficher son nom, sans liste déroulante
  if (models.length <= 1) {
    return (
      <span
        className="model-selector model-selector--static"
        title={selectedUnavailable ? unavailableTooltip(selectedModel) : selectedModel.description || undefined}
      >
        <ModelIcon />
        <span className="model-selector__current">{selectedModel.label}</span>
        {selectedUnavailable && <UnavailableBadge model={selectedModel} />}
      </span>
    );
  }

  const handleSelect = (option) => {
    // Modèle indisponible : non cliquable, le menu reste ouvert
    if (option.disabled) return;
    onSelect(option.id);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onTriggerKeyDown = (event) => {
    if (disabled || (event.key !== 'ArrowDown' && event.key !== 'ArrowUp')) return;
    event.preventDefault();
    if (open) {
      optionRefs.current[initialFocusIndex(options)]?.focus();
    } else {
      focusOnOpenRef.current = true;
      setOpen(true);
    }
  };

  // Flèches, Début et Fin : seulement entre les options activables
  const onMenuKeyDown = (event) => {
    const current = optionRefs.current.indexOf(document.activeElement);
    let next = null;
    if (event.key === 'ArrowDown') next = nextEnabledIndex(options, current, 1);
    else if (event.key === 'ArrowUp') next = nextEnabledIndex(options, current === -1 ? 0 : current, -1);
    else if (event.key === 'Home') next = nextEnabledIndex(options, -1, 1);
    else if (event.key === 'End') next = nextEnabledIndex(options, options.length, -1);
    if (next === null) return;
    event.preventDefault();
    if (next >= 0) optionRefs.current[next]?.focus();
  };

  let triggerTitle = 'Choisir le modèle';
  if (disabled) triggerTitle = 'Changement de modèle indisponible pendant une réponse';
  else if (selectedUnavailable) triggerTitle = `Choisir le modèle — ${unavailableTooltip(selectedModel)}`;

  return (
    <div className="model-selector" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        className="model-selector__trigger"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        title={triggerTitle}
      >
        <ModelIcon />
        <span className="model-selector__current">{selectedModel.label}</span>
        {selectedUnavailable && <UnavailableBadge model={selectedModel} />}
        <svg
          className={`model-selector__chevron ${open ? 'model-selector__chevron--open' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <path d="M18 15l-6-6-6 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            className="model-selector__menu"
            role="menu"
            aria-label="Modèles disponibles"
            onKeyDown={onMenuKeyDown}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.15 }}
          >
            {options.map((option, i) => {
              // Raison d'indisponibilité : description accessible de l'option, portée par un
              // élément caché HORS du bouton (dans le bouton, elle entrerait aussi dans son nom
              // et serait lue deux fois) ; aria-describedby lit un élément caché qu'il cible
              const describedBy = option.disabled ? `${reasonId}-${i}` : undefined;
              return (
                <li key={option.id} role="none">
                  <button
                    ref={(el) => { optionRefs.current[i] = el; }}
                    type="button"
                    role="menuitemradio"
                    aria-checked={option.selected}
                    aria-disabled={option.disabled || undefined}
                    aria-describedby={describedBy}
                    tabIndex={option.disabled ? -1 : undefined}
                    title={option.tooltip ?? undefined}
                    className={[
                      'model-selector__option',
                      option.selected ? 'model-selector__option--selected' : '',
                      option.disabled ? 'model-selector__option--unavailable' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => handleSelect(option)}
                  >
                    <span className="model-selector__option-text">
                      <span className="model-selector__label-row">
                        <span className="model-selector__label">{option.label}</span>
                        {option.disabled && (
                          <Badge tone="warn" className="model-selector__badge" aria-hidden="true">{UNAVAILABLE_LABEL}</Badge>
                        )}
                      </span>
                      {option.description && (
                        <span className="model-selector__description">{option.description}</span>
                      )}
                    </span>
                    {option.selected && (
                      <svg className="model-selector__check" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M20 6L9 17l-5-5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                  {option.disabled && <span id={describedBy} hidden>{option.tooltip}</span>}
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}
